/* ============================================================
   Demo.jsx — övningsläget: knappen i kioskens topplist, sifferblocket
   som låser upp det, och remsan som påminner om att inget sparas.
   ============================================================ */

import { useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { Modal } from '../components/ui.jsx';
import { demo } from '../store/store.js';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

/** Fyra siffror på ett sifferblock — snabbare än tangentbordet på en padda. */
export function DemoPinDialog({ onClose }) {
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function unlock(code) {
    setBusy(true);
    setErr('');
    try {
      await demo.start(code);
      onClose();
    } catch (e) {
      setErr(e.message || 'Fel demokod, försök igen.');
      setPin('');
    } finally {
      setBusy(false);
    }
  }

  function press(digit) {
    if (busy || pin.length >= 4) return;
    const next = pin + digit;
    setErr('');
    setPin(next);
    /* Fjärde siffran skickar direkt — ingen extra OK-knapp att leta efter. */
    if (next.length === 4) unlock(next);
  }

  const key = (label, onClick, style) => (
    <button key={label} type="button" onClick={onClick} disabled={busy}
      style={{
        height: 64, border: '1.5px solid var(--line)', borderRadius: 14, background: '#fff',
        fontFamily: 'var(--display)', fontWeight: 800, fontSize: 24, color: 'var(--green-deep)',
        cursor: busy ? 'default' : 'pointer', opacity: busy ? .5 : 1, ...style,
      }}>{label}</button>
  );

  return (
    <Modal title="Starta demoläge" onClose={onClose}>
      <p style={{ margin: '0 0 18px', color: 'var(--ink-soft)', fontSize: 14.5, lineHeight: 1.55, fontWeight: 600 }}>
        I demoläget kan ni öva på allt i kiosken — slå in köp, lämna ut gratis till
        domare och ledare, skriva noteringar och registrera svinn. <b>Ingenting sparas</b>, och när ni avslutar
        demot är appen tillbaka som den var.
      </p>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 14, margin: '4px 0 18px' }}>
        {[0, 1, 2, 3].map(i => (
          <div key={i} style={{
            width: 18, height: 18, borderRadius: 999,
            background: i < pin.length ? 'var(--green)' : 'transparent',
            border: `2px solid ${i < pin.length ? 'var(--green)' : 'var(--line)'}`,
          }} />
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, maxWidth: 300, margin: '0 auto' }}>
        {KEYS.map(k => key(k, () => press(k)))}
        {key('Rensa', () => { setPin(''); setErr(''); }, { fontSize: 15 })}
        {key('0', () => press('0'))}
        {key('⌫', () => { setPin(p => p.slice(0, -1)); setErr(''); })}
      </div>

      {err && (
        <div style={{
          background: '#fbeae7', color: 'var(--bad)', borderRadius: 10, padding: '10px 13px',
          fontSize: 13.5, fontWeight: 700, marginTop: 16, textAlign: 'center',
        }}>{err}</div>
      )}
      {busy && !err && (
        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13.5, fontWeight: 700, color: 'var(--muted)' }}>
          Kollar koden…
        </div>
      )}
    </Modal>
  );
}

/** Topplistens knapp: låser upp demot, och stänger av det utan kod. */
export function DemoButton({ active, onStart, onStop }) {
  if (active) {
    return (
      <button className="btn btn-orange" onClick={onStop} title="Avsluta demoläget">
        <Icon name="play" size={18} /> Avsluta DEMO
      </button>
    );
  }
  return (
    <button className="btn btn-ghost" onClick={onStart} title="Öva utan att något sparas">
      <Icon name="play" size={18} /> Demo
    </button>
  );
}

/** Omöjlig att missa: så länge remsan syns hamnar ingenting i databasen. */
export function DemoBar({ onStop }) {
  return (
    <div className="demo-bar">
      <span className="demo-bar-tag">DEMO</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        Övningsläge — ingenting du gör nu sparas. Kassan, lagret och noteringarna
        är tillbaka som de var när du avslutar.
      </span>
      <button className="btn btn-sm" onClick={onStop}
        style={{ background: '#fff', color: 'var(--orange-deep)', flexShrink: 0 }}>Avsluta</button>
    </div>
  );
}
